import { ExternalLink, Mail, Paperclip, Pencil, Plus, RefreshCw, Search, Sparkles, Tag, Check, X } from "lucide-react"
import { useState } from "preact/hooks"
import useSWR from "swr"
import { format, formatDistanceToNow, parseISO } from "date-fns"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { AiParseResponse, aiImportParseAttachment } from "@/api/ai_import"
import {
  EmailRemittanceFile,
  EmailSyncStatus,
  LinkedDeduction,
  getEmailSyncStatus,
  listEmailRemittances,
  startEmailSync,
} from "@/api/email_remittance"
import { EmailSourceJson, listEmailSources, updateEmailSource } from "@/api/email_source"
import { AiImportReview } from "@/file_imports/ai_import_review"
import { getFileImportDownloadUrl } from "@/api/file_import"
import { AttachToDeductionDialog } from "@/file_imports/attach_to_deduction_dialog"
import {
  DistributorSourceDialog,
  EmailSourceTemplate,
  sender_entries_of,
} from "@/file_imports/distributor_source_dialog"
import { toast } from "@/components/ui/use-toast"
import { use_debounced_value } from "@/hooks/use_debounced_value"
import { UserState } from "@/auth/user"
import { exactCurrencyFormatter } from "@/utils"

interface EmailRemittancesProps {
  /** Called after a confirmed import so the page can refresh its history. */
  onImported: () => void
  /** Fired when a picked distributor source carries a default deduction method. */
  onSourceDefaultMethod?: (method: string) => void
  className?: string
}

const SEARCH_DEBOUNCE_MS = 250
const ALL_SOURCES = "__all__"

/**
 * Email attachments default to `DEDUCT`, not the upload card's `Pay Customer Directly`.
 *
 * A remittance that arrives by email is a distributor explaining money they have **already taken**;
 * a check request is the brand asking to be paid, which is what you upload by hand. The two paths
 * used to share one radio — and that radio lives in the upload card, so someone parsing an email
 * could not see what it was set to, and got pending check requests without knowing why. A source's
 * own `default_custom_deduction_method` still wins, and the review's Method radio still overrides.
 */
const EMAIL_DEFAULT_DEDUCTION_METHOD = "DEDUCT"


/** How stale the list is, in the fewest words that still answer "should I sync?". */
export function sync_label(status?: EmailSyncStatus | null): string {
  if (!status) return ""
  if (status.running) return "Syncing now…"
  if (status.mailboxes.length === 0) return "No mailbox connected"
  if (!status.last_completed_at) return "Never synced"
  const when = `Synced ${formatDistanceToNow(parseISO(status.last_completed_at), { addSuffix: true })}`
  // A mailbox that has never run is a connection problem, not staleness — but it shouldn't drag the
  // headline, because orgs accumulate duplicate integration rows for one real mailbox.
  return status.never_synced > 0 ? `${when} · ${status.never_synced} never synced` : when
}

/** Per-mailbox detail on hover, because a brand with two mailboxes is only as fresh as its stalest. */
export function sync_tooltip(status?: EmailSyncStatus | null): string {
  if (!status || status.mailboxes.length === 0) return "Connect a mailbox to sync email"
  return status.mailboxes
    .map(m => {
      const when = m.last_completed_at
        ? formatDistanceToNow(parseISO(m.last_completed_at), { addSuffix: true })
        : "never synced"
      return `${m.username}: ${m.running ? "syncing now" : when}`
    })
    .join("\n")
}

/** Rows visible under a source filter ("" / __all__ = everything). */
export function filter_by_source(files: EmailRemittanceFile[], source_id: string): EmailRemittanceFile[] {
  if (!source_id || source_id === ALL_SOURCES) return files
  return files.filter((f: EmailRemittanceFile) => f.email_source_ids.includes(source_id))
}

/** Gmail-label palette: a stable color per source name so chips read consistently. */
const CHIP_PALETTES = [
  "bg-blue-50 text-blue-700 border-blue-200",
  "bg-green-50 text-green-700 border-green-200",
  "bg-purple-50 text-purple-700 border-purple-200",
  "bg-orange-50 text-orange-700 border-orange-200",
  "bg-teal-50 text-teal-700 border-teal-200",
  "bg-pink-50 text-pink-700 border-pink-200",
] as const

/**
 * Deductions before invoice payments, order otherwise preserved.
 *
 * One remittance books far more payments than charges — an SGWS advice yields ten invoice rows and
 * two deductions — and only the first four are shown. Without this, the charges the user came to
 * look at fall off the end behind payments they already expected.
 */
export function deductions_first(links: LinkedDeduction[]): LinkedDeduction[] {
  return [
    ...links.filter((link: LinkedDeduction) => link.kind === "deduction"),
    ...links.filter((link: LinkedDeduction) => link.kind !== "deduction"),
  ]
}

export function source_chip_classes(name: string): string {
  let hash = 0
  for (const ch of name.toLowerCase()) {
    hash = (hash * 31 + ch.charCodeAt(0)) >>> 0
  }
  return CHIP_PALETTES[hash % CHIP_PALETTES.length]
}

/**
 * Remittance-looking attachments from synced email, with a one-click AI parse.
 *
 * Files that have already been read or booked are listed last rather than hidden — re-parsing after
 * a parser fix is a real thing to want, and a file can be "parsed" by an OCR pass that produced
 * nothing. The badge says which case it is.
 *
 * Distributor sources work like Gmail labels here: emails matching a configured source wear its
 * chip, the picker filters the list to one source, and each row's tag menu can add the email's
 * sender to an existing source or spin up a new source using the email as a template.
 */
export function EmailRemittances({ onImported, onSourceDefaultMethod, className = "" }: EmailRemittancesProps) {
  const user = UserState.use()
  const is_system_admin = user?.role === "system_admin"

  const [search, setSearch] = useState("")
  const [deductionMethod, setDeductionMethod] = useState<string>(EMAIL_DEFAULT_DEDUCTION_METHOD)
  const [parseError, setParseError] = useState<string | null>(null)
  const [parsingId, setParsingId] = useState<string | null>(null)
  const [parseResponse, setParseResponse] = useState<AiParseResponse | null>(null)
  const [parsedFile, setParsedFile] = useState<EmailRemittanceFile | null>(null)
  const [attaching, setAttaching] = useState<EmailRemittanceFile | null>(null)

  const [selectedSourceId, setSelectedSourceId] = useState<string>(ALL_SOURCES)
  const [sourceDialog, setSourceDialog] = useState<{
    open: boolean
    source: EmailSourceJson | null
    template: EmailSourceTemplate | null
  }>({ open: false, source: null, template: null })

  const { data: sources, mutate: mutateSources } = useSWR<EmailSourceJson[]>("/email-source", listEmailSources)

  // Mailboxes sync on a weekday cron, so "nothing here" is ambiguous without knowing how stale the
  // list is. Poll while a sync is running so the button and the timestamp settle on their own.
  const [syncing, setSyncing] = useState(false)
  const { data: syncStatus, mutate: mutateSync } = useSWR<EmailSyncStatus>("/file-imports/email-sync", getEmailSyncStatus, {
    refreshInterval: (status?: EmailSyncStatus) => (status?.running ? 5000 : 0),
  })

  // Search runs server-side, so a keystroke shouldn't be a request — debounce the value that goes
  // into the SWR key rather than the fetch, and swr handles caching, dedup and revalidation.
  const debounced_search = use_debounced_value(search, SEARCH_DEBOUNCE_MS)
  const {
    data: files,
    error: loadError,
    isLoading,
    mutate,
  } = useSWR<EmailRemittanceFile[]>(`/file-imports/email-remittances?q=${encodeURIComponent(debounced_search)}`, () =>
    listEmailRemittances({ q: debounced_search || undefined })
  )
  const error = parseError ?? (loadError ? "Could not load email remittances" : null)

  const selectedSource = (sources ?? []).find((s: EmailSourceJson) => s.id === selectedSourceId) ?? null

  function handleSourceSelected(id: string) {
    setSelectedSourceId(id)
    const source = (sources ?? []).find((s: EmailSourceJson) => s.id === id)
    const method = source?.default_custom_deduction_method
    if (method) {
      setDeductionMethod(method)
      onSourceDefaultMethod?.(method)
    }
  }

  async function handleSyncEmail() {
    setSyncing(true)
    try {
      const status = await startEmailSync()
      await mutateSync(status, { revalidate: false })
      toast({ title: "Email sync started", description: "New attachments appear here as mailboxes finish." })
    } catch (e) {
      toast({ title: e instanceof Error ? e.message : "Could not start an email sync" })
    } finally {
      setSyncing(false)
    }
    void mutate()
  }

  function can_edit_source(source: EmailSourceJson): boolean {
    return !source.is_global || is_system_admin
  }

  /** Tag-menu action: teach an existing source this email's sender. */
  async function handleAddToSource(file: EmailRemittanceFile, source: EmailSourceJson) {
    const sender = file.from_email?.toLowerCase()
    if (!sender) {
      toast({ title: "This email has no sender address to add" })
      return
    }
    try {
      await updateEmailSource(source.id, { sender_entries: [...sender_entries_of(source), sender] })
      toast({ title: `Added ${sender} to ${source.name}` })
      await mutateSources()
      await mutate()
    } catch (e) {
      toast({ title: e instanceof Error ? e.message : `Could not update ${source.name}` })
    }
  }

  async function handleParse(file: EmailRemittanceFile, force: boolean) {
    setParsingId(file.attachment_id)
    setParseError(null)
    try {
      const response = await aiImportParseAttachment(file.attachment_id, deductionMethod, force)
      setParseResponse(response)
      setParsedFile(file)
    } catch (e) {
      setParseError(e instanceof Error ? e.message : "Could not parse that file")
    } finally {
      setParsingId(null)
    }
  }

  function handleReset() {
    setParseResponse(null)
    setParsedFile(null)
    onImported()
    void mutate()
  }

  const rows = filter_by_source(files ?? [], selectedSourceId)
  const unhandled = rows.filter(f => !f.already_parsed).length

  return (
    <>
      {/* The review sits above the list rather than replacing it. Parsing one attachment out of a
          mailbox is rarely a single-shot task — you work down the list — and swapping the list out
          for the review meant losing your place, your filter and your scroll position on every file. */}
      {parseResponse && (
        <div class={`bg-white rounded-lg shadow mb-6`}>
          <div class="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
            <div class="min-w-0">
              <h2 class="text-lg font-semibold truncate">{parsedFile?.name}</h2>
              <p class="text-sm text-gray-500 mt-1 truncate">{parsedFile?.subject}</p>
            </div>
            <Button variant="ghost" size="sm" class="shrink-0" onClick={handleReset} title="Close this review">
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div class="p-6">
            <AiImportReview parseResponse={parseResponse} onReset={handleReset} />
          </div>
        </div>
      )}
    <div class={`bg-white rounded-lg shadow ${className}`}>
      <div class="px-6 py-4 border-b border-gray-200">
        <div class="flex items-center gap-2">
          <Mail className="h-5 w-5 text-blue-500" />
          <h2 class="text-lg font-semibold">Remittances in your email</h2>
          {unhandled > 0 && (
            <span class="text-xs font-medium bg-blue-50 text-blue-700 rounded-full px-2 py-0.5">
              {unhandled} not yet parsed
            </span>
          )}
          {/* Mailboxes sync on a daily cron, so an empty list is ambiguous — is the file missing, or
              just not synced yet? Saying how stale the list is answers that without a support ticket. */}
          <div class="ml-auto flex items-center gap-3">
            <span class="text-xs text-gray-500" title={sync_tooltip(syncStatus)}>
              {sync_label(syncStatus)}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={syncing || syncStatus?.running}
              onClick={handleSyncEmail}>
              <RefreshCw className={`h-3.5 w-3.5 ${syncing || syncStatus?.running ? "animate-spin" : ""}`} />
              {syncing || syncStatus?.running ? "Syncing…" : "Sync email"}
            </Button>
          </div>
        </div>
        <p class="text-sm text-gray-500 mt-1">
          Payment advices, remittances and deduction backup we found on synced email threads. Files already read or
          booked are listed last. Your own bank's advices are excluded. Emails wear the label of any distributor
          source they match — use the tag menu on a row to label more.
        </p>
        <div class="flex flex-wrap items-center gap-2 mt-3">
          <Select value={selectedSourceId} onValueChange={handleSourceSelected}>
            <SelectTrigger className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_SOURCES}>All distributor sources</SelectItem>
              {(sources ?? []).map((s: EmailSourceJson) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                  {s.is_global ? "" : " (custom)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedSource && can_edit_source(selectedSource) && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setSourceDialog({ open: true, source: selectedSource, template: null })}>
              <Pencil className="h-3.5 w-3.5" /> Edit
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => setSourceDialog({ open: true, source: null, template: null })}>
            <Plus className="h-3.5 w-3.5" /> Add distributor source
          </Button>
        </div>
        <div class="relative mt-3">
          <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={search}
            placeholder="Search by sender, subject or filename"
            className="pl-9"
            onInput={(e: Event) => setSearch((e.target as HTMLInputElement).value)}
          />
        </div>
      </div>

      {error && <div class="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>}

      <div class="divide-y divide-gray-200">
        {isLoading ? (
          <div class="p-6 text-center text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div class="p-6 text-center text-gray-500">
            {selectedSource
              ? `No remittance-looking attachments match ${selectedSource.name}'s sender or subject patterns.`
              : search
                ? "No remittance-looking attachments match that search."
                : "No remittance-looking attachments found in synced email."}
          </div>
        ) : (
          rows.map(file => (
            <EmailRemittanceRow
              key={file.attachment_id}
              file={file}
              sources={sources ?? []}
              canEditSource={can_edit_source}
              parsing={parsingId === file.attachment_id}
              disabled={parsingId !== null}
              onParse={handleParse}
              onAttach={setAttaching}
              onFilterSource={handleSourceSelected}
              onAddToSource={handleAddToSource}
              onCreateSource={(template: EmailSourceTemplate) => setSourceDialog({ open: true, source: null, template })}
            />
          ))
        )}
      </div>

      <AttachToDeductionDialog file={attaching} onClose={() => setAttaching(null)} onAttached={() => void mutate()} />

      <DistributorSourceDialog
        open={sourceDialog.open}
        source={sourceDialog.source}
        template={sourceDialog.template}
        onClose={() => setSourceDialog({ open: false, source: null, template: null })}
        onSaved={() => {
          void mutateSources()
          void mutate()
        }}
      />
    </div>
    </>
  )
}

interface EmailRemittanceRowProps {
  file: EmailRemittanceFile
  sources: EmailSourceJson[]
  canEditSource: (source: EmailSourceJson) => boolean
  parsing: boolean
  disabled: boolean
  onParse: (file: EmailRemittanceFile, force: boolean) => void
  onAttach: (file: EmailRemittanceFile) => void
  onFilterSource: (source_id: string) => void
  onAddToSource: (file: EmailRemittanceFile, source: EmailSourceJson) => void
  onCreateSource: (template: EmailSourceTemplate) => void
}

function EmailRemittanceRow({
  file,
  sources,
  canEditSource,
  parsing,
  disabled,
  onParse,
  onAttach,
  onFilterSource,
  onAddToSource,
  onCreateSource,
}: EmailRemittanceRowProps) {
  const sender = file.from_name || file.from_email || "Unknown sender"
  const when = file.date ? format(parseISO(file.date), "MMM d, yyyy") : ""
  // Same route the rest of the app uses for a stored file: a presigned redirect the browser opens
  // itself, so no viewer here has to know what a PDF, a scan or a spreadsheet looks like.
  const file_url = getFileImportDownloadUrl(file.s3_uri)

  const matched = file.email_source_ids
    .map((id: string) => sources.find((s: EmailSourceJson) => s.id === id))
    .filter((s): s is EmailSourceJson => s !== undefined)
  const unmatched = sources.filter((s: EmailSourceJson) => !file.email_source_ids.includes(s.id))

  return (
    <div
      class={`flex items-start justify-between gap-4 px-6 py-4 ${
        file.is_duplicate ? "bg-gray-50 opacity-60" : file.already_parsed ? "bg-gray-50/60" : ""
      }`}>
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-2 flex-wrap">
          <a
            href={file_url}
            target="_blank"
            rel="noreferrer"
            class="font-medium text-gray-900 truncate hover:text-blue-600 hover:underline">
            {file.name}
          </a>
          {matched.map((source: EmailSourceJson) => (
            // The Gmail-label chip: this email matches the source's sender/subject
            // patterns. Clicking filters the list to that source.
            <button
              key={source.id}
              type="button"
              class={`text-xs border rounded-full px-2 py-0.5 shrink-0 ${source_chip_classes(source.name)}`}
              title={`Matches the ${source.name} distributor source — click to filter`}
              onClick={() => onFilterSource(source.id)}>
              {source.name}
            </button>
          ))}
          {!file.strong && (
            // A statement of account is as often a supplier chasing AP as a customer explaining a
            // payment, and no keyword separates them — so say which kind of match this is rather
            // than presenting both with equal confidence.
            <span class="text-xs bg-amber-50 text-amber-700 rounded-full px-2 py-0.5 shrink-0" title="Matched only on an ambiguous signal — this may be ordinary paperwork rather than a remittance.">
              possible
            </span>
          )}
          {file.already_parsed && (
            // Green only when the parse actually produced transactions. "Already read, nothing
            // booked" and "classified as a check" are states to notice, not successes, so they
            // stay grey — colouring them the same would make a file that yielded nothing look
            // done.
            <span
              class={`text-xs rounded-full px-2 py-0.5 shrink-0 ${
                file.linked_deduction_count > 0
                  ? "bg-green-100 text-green-800 border border-green-200 font-medium"
                  : "bg-gray-200 text-gray-700"
              }`}>
              {file.parsed_reason ?? "already parsed"}
            </span>
          )}
          {file.is_duplicate && (
            // The same advice reaches the mailbox from the sender, then again on every FW: and
            // Re: of its thread. Each copy is a separate stored file, so the list would otherwise
            // show three identical rows with no hint which one to act on.
            <span
              class="text-xs bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5 shrink-0"
              title="Another copy of this same document is listed above — act on that one.">
              duplicate
            </span>
          )}
        </div>
        <div class="text-sm text-gray-600 truncate mt-0.5">{file.subject}</div>
        <div class="text-xs text-gray-400 mt-1">
          {sender}
          {when ? ` · ${when}` : ""}
          {file.signals.length > 0 ? ` · matched ${file.signals.join(", ")}` : ""}
        </div>
        {file.linked_deductions?.length > 0 && (
          // The whole point of parsing a row is the deductions it creates; without a link back the
          // user has to go and search for what their own click just made.
          <div class="text-xs text-gray-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>Created</span>
            {/* Charges first: an advice books far more invoice payments than deductions, and the
                deductions are what the user is here for. */}
            {deductions_first(file.linked_deductions)
              .slice(0, 4)
              .map((deduction: LinkedDeduction) => (
                <a
                  key={deduction.id}
                  href={`/deduction/${deduction.id}`}
                  class={`hover:underline inline-flex items-center gap-1 ${
                    deduction.kind === "deduction"
                      ? "text-indigo-600 hover:text-indigo-800 font-medium"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                  title={
                    deduction.kind === "deduction"
                      ? "Open the deduction this file produced"
                      : "Open the invoice payment this file produced"
                  }>
                  {deduction.invoice_number || "(no invoice #)"}
                  <span class="text-gray-400">{exactCurrencyFormatter(deduction.invoice_amount)}</span>
                </a>
              ))}
            {file.linked_deductions.length > 4 && <span>+{file.linked_deductions.length - 4} more</span>}
          </div>
        )}
      </div>
      <div class="flex items-center gap-2 shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" class="gap-1.5" title="Label this email with a distributor source">
              <Tag className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel>Distributor source</DropdownMenuLabel>
            {matched.map((source: EmailSourceJson) => (
              <DropdownMenuItem key={source.id} onSelect={() => onFilterSource(source.id)}>
                <Check className="h-4 w-4 mr-2 text-green-600" />
                {source.name}
              </DropdownMenuItem>
            ))}
            {unmatched.filter(canEditSource).map((source: EmailSourceJson) => (
              <DropdownMenuItem key={source.id} onSelect={() => onAddToSource(file, source)}>
                <span class="w-4 mr-2" />
                Add sender to {source.name}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() =>
                onCreateSource({ message_id: file.message_id, from_email: file.from_email ?? null, subject: file.subject })
              }>
              <Plus className="h-4 w-4 mr-2" />
              Create source from this email…
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="outline" size="sm" class="gap-1.5" asChild>
          <a href={file_url} target="_blank" rel="noreferrer">
            <ExternalLink className="h-4 w-4" />
            Open
          </a>
        </Button>
        <Button variant="outline" size="sm" class="gap-1.5" onClick={() => onAttach(file)}>
          <Paperclip className="h-4 w-4" />
          Attach
        </Button>
        <Button
          variant={file.already_parsed ? "outline" : "default"}
          size="sm"
          class="gap-1.5"
          disabled={disabled}
          onClick={() => onParse(file, file.linked_deduction_count > 0)}>
          <Sparkles className="h-4 w-4" />
          {parsing ? "Parsing…" : file.already_parsed ? "Parse again" : "Parse with AI"}
        </Button>
      </div>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  function file_with(id: string, email_source_ids: string[]): EmailRemittanceFile {
    return { attachment_id: id, email_source_ids } as EmailRemittanceFile
  }

  function status(over: Partial<EmailSyncStatus> = {}): EmailSyncStatus {
    return { mailboxes: [], last_completed_at: null, never_synced: 0, running: false, ...over }
  }
  function mailbox(username: string, last_completed_at: string | null, running = false) {
    return { integration_id: username, provider: "gmail", username, last_completed_at, running }
  }

  describe("sync_label", () => {
    it("distinguishes never-synced from no-mailbox", () => {
      // "Never synced" on a brand with no mailbox reads like a broken sync rather than a missing
      // connection, and sends them to the wrong page to fix it.
      expect(sync_label(status())).toBe("No mailbox connected")
      expect(sync_label(status({ mailboxes: [mailbox("ap@brand.com", null)], never_synced: 1 }))).toBe("Never synced")
    })

    it("a stale duplicate integration does not hide a sync that really ran", () => {
      // Esti carries `Accounting@` and `accounting@` for one mailbox; letting the dead row win would
      // report "Never synced" over a sync from an hour ago.
      const label = sync_label(
        status({
          last_completed_at: new Date(Date.now() - 3600_000).toISOString(),
          never_synced: 1,
          mailboxes: [mailbox("a@b.com", new Date(Date.now() - 3600_000).toISOString()), mailbox("A@b.com", null)],
        })
      )
      expect(label).toContain("Synced")
      expect(label).toContain("1 never synced")
    })

    it("says so while a sync is running", () => {
      expect(sync_label(status({ running: true, mailboxes: [mailbox("ap@brand.com", null, true)] }))).toBe("Syncing now…")
    })

    it("renders nothing before the status has loaded", () => {
      expect(sync_label(undefined)).toBe("")
    })
  })

  describe("sync_tooltip", () => {
    it("lists each mailbox, because the summary is only as fresh as the stalest", () => {
      const tip = sync_tooltip(
        status({ mailboxes: [mailbox("ap@brand.com", "2026-07-30T10:00:00Z"), mailbox("ar@brand.com", null)] })
      )
      expect(tip).toContain("ap@brand.com")
      expect(tip).toContain("ar@brand.com: never synced")
    })

    it("points at connecting a mailbox when there are none", () => {
      expect(sync_tooltip(status())).toBe("Connect a mailbox to sync email")
    })
  })

  describe("filter_by_source", () => {
    it("passes everything through for the all-sources filter", () => {
      const files = [file_with("a", ["s1"]), file_with("b", [])]
      expect(filter_by_source(files, "__all__")).toEqual(files)
      expect(filter_by_source(files, "")).toEqual(files)
    })

    it("keeps only files matching the selected source", () => {
      const files = [file_with("a", ["s1", "s2"]), file_with("b", ["s2"]), file_with("c", [])]
      expect(filter_by_source(files, "s1").map(f => f.attachment_id)).toEqual(["a"])
      expect(filter_by_source(files, "s2").map(f => f.attachment_id)).toEqual(["a", "b"])
      expect(filter_by_source(files, "s3")).toEqual([])
    })
  })

  describe("source_chip_classes", () => {
    it("is stable per name and case-insensitive", () => {
      expect(source_chip_classes("UNFI")).toBe(source_chip_classes("unfi"))
      expect(source_chip_classes("Rainforest")).toBe(source_chip_classes("Rainforest"))
    })
  })
}
